/*
 * Scroll story controller — the homepage hero.
 *
 * Scroll-TRIGGERED, never scroll-hijacked: no wheel/touch/key interception,
 * no smooth-scroll shim, no snap. The page always responds natively; this
 * only reads scroll position and writes transforms.
 *
 * Which experience runs is decided in CSS, not here — a media query shows
 * the pinned track on wide screens with motion allowed, and the stacked
 * static tree everywhere else. So the correct layout is present on first
 * paint, survives JS failure, and never flashes.
 *
 * This file:
 *   desktop  scrubs the pinned sequence (one rAF loop, transform/opacity only)
 *   mobile   adds short entrance transitions to the stacked sections
 *   reduced  does nothing at all
 *
 * Colour finale (opt-in via the section setting): the bag bows out
 * entering the freeze/use/repeat phase, and four cubes float in from the
 * four viewport edges, staggered across that phase and the next. Each
 * cube is a STACK of photographs — one per colour the cube is really sold
 * in — and colour is chosen by which layer is on top, never by tinting a
 * shape. The blue cutout is the floor of the stack and the cube LANDS on
 * it, still blue; a conveyor then walks the row, dropping a new colour in
 * on the right and pushing the leftmost off, and the dock CTA gains
 * focusability. Scrolling back up reverses everything: the cycle stops,
 * every cube returns to blue, the cubes fly out, the bag returns.
 *
 * Work stops when the section leaves the viewport or the tab is hidden, and
 * everything is torn down on shopify:section:unload so the theme editor can
 * reload the section without stacking listeners.
 */
(function () {
  'use strict';

  var MOTION_QUERY = '(min-width: 900px) and (prefers-reduced-motion: no-preference)';
  var REDUCED_QUERY = '(prefers-reduced-motion: reduce)';
  var LUT_STEPS = 48; // per segment; colour is precomputed, never per frame
  var FLIP_MS = 1400; // dock colour cycle; first tick waits a full beat

  /*
   * The finale cubes have two arrangements and lerp between them.
   *
   *   PILE  a heap at the bag's FEET that it slides into as the story moves
   *         off "no meltwater" and onto freeze/use/repeat. Offsets (vw/vh)
   *         here pull each cube from its dock slot in toward a common heap
   *         and stagger it vertically, so the four overlap and stack the way
   *         a real pile does; the heap is then carried sideways onto the bag
   *         by --dock-x and dropped to its base by measurePile().
   *   DOCK  the spread row on the colour phase — offset 0, i.e. the flex
   *         row's own layout. `rest` is the settled tilt it keeps there,
   *         in the JS transform because a CSS rotate would be overwritten
   *         by the per-frame write.
   */
  /*
   * `ps` is the cube's size IN THE HEAP, as a fraction of its docked size.
   *
   * It has to be per-cube rather than one constant. The dock row is uniform
   * — it must be, because the conveyor slides cubes between slots and a
   * per-slot width would resize them mid-move — but a heap of four
   * identically-sized cubes reads as a stack of coins, and at wide viewports
   * the convergent vw offsets below pull them into a single clump. These
   * four numbers reproduce the sizes the heap had when each slot still had
   * its own width (87/100/128/92 px against a 144 px dock cube), so the
   * heap looks hand-piled while the row stays machine-uniform.
   *
   * `x` is how far each cube is pulled IN toward the heap from its dock slot,
   * and it is in vw while the dock pitch is px — so the wider the screen, the
   * harder the pull and the tighter the clump. They were tuned when the row
   * was narrower; against today's uniform 188px pitch they closed the cubes
   * into a stack. Roughly a third less convergence leaves them overlapping
   * like loose ice instead of shuffled into a pile of coins.
   */
  var PILE_SPOTS = [
    { x: 7.2, y: 2.6, r: -16, rest: -4, ps: 0.60 },
    { x: 2.6, y: -3.8, r: 12, rest: 3, ps: 0.69 },
    { x: -2.4, y: 3.0, r: -8, rest: -2, ps: 0.89 },
    { x: -7.0, y: -1.4, r: 20, rest: 5, ps: 0.64 }
  ];
  var PILE_SCALE = 0.78; // fallback for any cube without its own `ps`
  var PILE_DROP = 28;    // vh above the heap each cube falls in from
  // How much of the heap sits above the bag's base, as a fraction of its own
  // height — enough that the ice reads as piled AGAINST the bag rather than
  // resting on a shelf below it.
  var PILE_STRADDLE = 0.35;
  // How far right of the bag's centre the heap gathers, as a fraction of the
  // bag's rendered width, so the ice banks against its right side.
  var PILE_RIGHT = 0.28;

  var instances = [];

  // ---------------------------------------------------------------- colour --
  // Resolved once at mount into a lookup table. OKLCh (not RGB) because a
  // straight line between a saturated dark and a near-white passes through
  // grey — that was the muddy midpoint.
  function hexToRgb(h) {
    h = (h || '').replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (h.length !== 6) return null;
    var v = parseInt(h, 16);
    return isNaN(v) ? null : [(v >> 16) & 255, (v >> 8) & 255, v & 255];
  }

  function toLinear(c) {
    c /= 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  function toSrgb(c) {
    c = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(c * 255)));
  }

  function rgbToOklab(c) {
    var r = toLinear(c[0]), g = toLinear(c[1]), b = toLinear(c[2]);
    var l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    var m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    var s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [
      0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
      1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
      0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s
    ];
  }

  function oklabToRgb(lab) {
    var l_ = lab[0] + 0.3963377774 * lab[1] + 0.2158037573 * lab[2];
    var m_ = lab[0] - 0.1055613458 * lab[1] - 0.0638541728 * lab[2];
    var s_ = lab[0] - 0.0894841775 * lab[1] - 1.2914855480 * lab[2];
    var l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
    return [
      toSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
      toSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
      toSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s)
    ];
  }

  function mixRgb(a, b, t) {
    var A = rgbToOklab(a), B = rgbToOklab(b);
    var cA = Math.sqrt(A[1] * A[1] + A[2] * A[2]);
    var cB = Math.sqrt(B[1] * B[1] + B[2] * B[2]);
    var hA = Math.atan2(A[2], A[1]);
    var hB = Math.atan2(B[2], B[1]);
    if (cA < 0.002) hA = hB;
    if (cB < 0.002) hB = hA;
    var dh = hB - hA;
    if (dh > Math.PI) dh -= 2 * Math.PI;
    if (dh < -Math.PI) dh += 2 * Math.PI;
    var L = A[0] + (B[0] - A[0]) * t;
    var h = hA + dh * t;
    // Chroma held up over the seam so the middle stays a colour, not a grey.
    var C = (cA + (cB - cA) * t) * (1 + 0.45 * Math.sin(Math.PI * t));
    return oklabToRgb([L, Math.cos(h) * C, Math.sin(h) * C]);
  }

  function smoothstep(t) {
    return t * t * (3 - 2 * t);
  }

  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  // ------------------------------------------------------------ controller --
  function ScrollStory(root) {
    this.root = root;
    this.mode = null;
    this.running = false;
    this.rafPending = false;
    this.inView = true;

    // Cached once; nothing is queried during scroll.
    this.track = root.querySelector('[data-sstory-track]');
    this.bag = root.querySelector('[data-bag]');
    this.bgFill = root.querySelector('[data-bg-fill]');
    this.chip = root.querySelector('[data-chip]');
    this.cubesBg = root.querySelector('[data-cubes-bg]');
    this.heroCubes = [].slice.call(root.querySelectorAll('[data-hero-cube]'));
    this.copyLayers = [].slice.call(root.querySelectorAll('[data-copy-layer]'));
    this.elementLayers = [].slice.call(root.querySelectorAll('[data-element-layer]'));
    this.staticBands = [].slice.call(root.querySelectorAll('[data-static-band]'));
    this.pin = root.querySelector('.sstory__pin');
    this.solo = root.querySelector('[data-solo]');
    this.soloCta = root.querySelector('.sstory__solo-cta');
    this.pngCubes = [].slice.call(root.querySelectorAll('[data-png-cube]'));
    this.cubeFlights = [].slice.call(root.querySelectorAll('[data-cube-flight]'));
    this.cubeRow = root.querySelector('.sstory__solo-cubes');
    // Slots in their ORIGINAL order. The conveyor reorders the live DOM, so
    // this is what teardown restores to put the row back how it shipped.
    this.cubeSlots = [].slice.call(root.querySelectorAll('[data-cube-slot]'));
    // Per cube, its colour layers in cycle order. The section renders the
    // same list into every cube, so the length IS the palette length.
    this.cubeSkins = this.pngCubes.map(function (c) {
      return [].slice.call(c.querySelectorAll('[data-cube-skin]'));
    });
    this.skinCount = this.cubeSkins.length ? this.cubeSkins[0].length : 0;
    // How many of the slots are actually ON the row. The last one is the
    // conveyor's on-deck spare, and it takes no part in the flight or the
    // pile — everything below that measures or staggers the DOCK, not the
    // slot count.
    this.dockSize = Math.min(PILE_SPOTS.length, Math.max(this.pngCubes.length - 1, 1));

    this.n = Math.max(Number(root.dataset.phaseCount) || 0, 2);
    this.intensity = (Number(root.dataset.intensity) || 60) / 100;
    this.rotateOn = root.dataset.rotate === 'true';
    this.bgOn = root.dataset.bg === 'true';
    this.cubesTravel = isNaN(Number(root.dataset.cubesParallax)) ? 22 : Number(root.dataset.cubesParallax);
    this.cubesAfter = (Number(root.dataset.cubesAfter) || 0) / 100;
    this.baseCubeOpacity = this.cubesBg ? (parseFloat(this.cubesBg.style.opacity) || 1) : 1;
    this.frames = [];

    // Three conditions, because the finale rewrites the ending: the setting,
    // the Liquid-gated markup, and a last phase actually built as the dock
    // (centre-top copy). Ticking the checkbox WITHOUT adding that phase used
    // to retire the bag mid-story and leave the reserved bag half empty; now
    // a half-configured section simply degrades to the plain ending, and
    // adding the dock phase turns the finale on by itself.
    var dockCopy = this.copyLayers[this.copyLayers.length - 1];
    this.finale = root.dataset.finale === 'true' && !!this.solo &&
      !!dockCopy && dockCopy.classList.contains('sstory__copy--center');
    this.cubeEnts = []; // per-cube entry progress, written every frame
    this.flipIdx = 0;
    this.flipTimer = null;
    this.flipPainted = false; // has the conveyor taken over from the landing
    this.landIdx = [];  // the colour each docking cube morphs into
    this.landKeys = []; // last arrival step written per cube, so repeats skip
    this.slideOpen = false;   // is a conveyor move still sitting un-committed

    this.buildColorTable();

    this.motionMQ = window.matchMedia(MOTION_QUERY);
    this.reducedMQ = window.matchMedia(REDUCED_QUERY);

    this.onScroll = this.requestUpdate.bind(this);
    this.onResize = this.handleResize.bind(this);
    this.onVisibility = this.handleVisibility.bind(this);
    this.onModeChange = this.evaluateMode.bind(this);
    this.tick = this.update.bind(this);

    this.addMQListener(this.motionMQ, this.onModeChange);
    this.addMQListener(this.reducedMQ, this.onModeChange);
    this.evaluateMode();
  }

  ScrollStory.prototype.addMQListener = function (mq, fn) {
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else if (mq.addListener) mq.addListener(fn); // older Safari
  };

  ScrollStory.prototype.removeMQListener = function (mq, fn) {
    if (mq.removeEventListener) mq.removeEventListener('change', fn);
    else if (mq.removeListener) mq.removeListener(fn);
  };

  // Colours are fixed at author time, so resolve the whole ramp once.
  ScrollStory.prototype.buildColorTable = function () {
    var stops = (this.root.dataset.phaseColors || '')
      .split(',')
      .map(function (c) { return hexToRgb(c); })
      .filter(Boolean);
    this.colorTable = null;
    if (stops.length < 2) return;
    var table = [];
    for (var i = 0; i < stops.length - 1; i++) {
      var segment = [];
      for (var s = 0; s <= LUT_STEPS; s++) {
        var rgb = mixRgb(stops[i], stops[i + 1], s / LUT_STEPS);
        segment.push('rgb(' + rgb[0] + ',' + rgb[1] + ',' + rgb[2] + ')');
      }
      table.push(segment);
    }
    this.colorTable = table;
  };

  ScrollStory.prototype.evaluateMode = function () {
    var next = this.reducedMQ.matches ? 'reduced'
      : this.motionMQ.matches ? 'desktop'
        : 'mobile';
    if (next === this.mode) return;
    this.teardownMode();
    this.mode = next;
    if (next === 'desktop') this.mountDesktop();
    else if (next === 'mobile') this.mountMobile();
    // 'reduced' does nothing on purpose: CSS already shows the static tree
    // in its final positions, with no animated colour.
  };

  // ------------------------------------------------------------- desktop ----
  ScrollStory.prototype.mountDesktop = function () {
    if (!this.track || !this.bag) return;
    // Before buildFrames: it measures the pile against the row, and the row
    // has to be in its shipped order for that to mean anything.
    if (this.finale) this.resetConveyor();
    this.buildFrames();

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('orientationchange', this.onResize, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);

    var self = this;
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        self.inView = entries[0].isIntersecting;
        self.setRunning(self.inView && !document.hidden);
      });
      this.io.observe(this.track);
    } else {
      this.inView = true;
      this.setRunning(true);
    }
    this.update(); // correct first frame, synchronously
  };

  // will-change is a promise to the compositor, not a free win: hold the
  // layers only while the sequence is actually being scrubbed.
  ScrollStory.prototype.setRunning = function (on) {
    if (on === this.running) return;
    this.running = on;
    this.root.classList.toggle('sstory--animating', on);
    if (on) this.requestUpdate();
    // Paused = off screen or hidden tab; the next update() restarts the
    // cycle if the cubes are still docked.
    else this.setFlip(false);
  };

  ScrollStory.prototype.handleVisibility = function () {
    this.setRunning(!document.hidden && this.inView !== false);
  };

  ScrollStory.prototype.handleResize = function () {
    clearTimeout(this.resizeTimer);
    var self = this;
    this.resizeTimer = setTimeout(function () {
      // A breakpoint change swaps the whole experience; otherwise just
      // rebuild keyframes, which bake in viewport-dependent offsets.
      self.evaluateMode();
      if (self.mode === 'desktop') {
        self.buildFrames();
        self.update();
      }
    }, 150);
  };

  ScrollStory.prototype.buildFrames = function () {
    var mobileSimple = this.root.dataset.mobileSimple === 'true';
    var narrow = window.matchMedia('(max-width: 1100px)').matches;
    var moveScale = this.intensity * (narrow && mobileSimple ? 0.7 : 1);
    var rotScale = this.rotateOn ? this.intensity : 0;

    this.frames = this.copyLayers.map(function (c, i) {
      var side = c.classList.contains('sstory__copy--right') ? 'right'
        : c.classList.contains('sstory__copy--center') ? 'center' : 'left';
      var x = side === 'left' ? 26 : side === 'right' ? -26 : 0;
      var rot = side === 'center' ? 0 : (i % 2 === 0 ? 4 : -4);
      var scale = side === 'center' ? 0.86 : 1 - i * 0.04;
      return {
        x: x * moveScale,
        y: i * 1.5 * moveScale,
        r: rot * rotScale,
        s: Math.max(scale, 0.74)
      };
    });
    if (!this.frames.length) this.frames.push({ x: 0, y: 0, r: 0, s: 1 });
    while (this.frames.length < this.n) this.frames.push(this.frames[this.frames.length - 1]);
    // Hero: bag sits right of the copy column, with a whisper of tilt.
    this.frames[0].x = 30 * moveScale;
    this.frames[0].s = 1;
    this.frames[0].r = 3 * rotScale;
    this.groupFrames();
    // Dock first: the pile is measured against where the dock row ends up.
    this.measureDock();
    // After groupFrames, which is what settles the bag's per-phase scale.
    this.measurePile();
  };

  /*
   * Where the dock row sits between the finale's heading and its button.
   *
   * The row and the button are one grid, so the only way to lift the row is
   * to open the gap between them — and then hand half of it back at the top,
   * or the button rides up too. Opening the gap by G moves the row up by
   * exactly G and leaves the button untouched under BOTH of the
   * stylesheet's anchorings: centred, because the compensating rise cancels
   * the growth, and bottom-anchored (short viewports), because there the
   * button IS the bottom edge and there is nothing to give back.
   *
   * Measured rather than tuned, because not one of the three edges is a
   * fixed fraction of anything: the heading is px-tall type hung at a
   * percentage, the button is px-tall at a percentage, and the row's height
   * follows cube widths that are a min() of a rem and a vw. One pair of
   * constants that centred the row perfectly at 900px tall was out by ~90px
   * at 1117px, and the client can retype the heading into a third line
   * whenever they like.
   */
  ScrollStory.prototype.measureDock = function () {
    if (!this.finale || !this.solo || !this.pin || !this.soloCta) return;
    var row = this.solo.querySelector('.sstory__solo-cubes');
    var copy = this.copyLayers[this.copyLayers.length - 1];
    if (!row || !copy) return;

    // Measure the composition at rest, not whatever the scrub is mid-way
    // through doing to it, and from a known-zero gap.
    var keepX = this.solo.style.getPropertyValue('--dock-x');
    var keepY = this.solo.style.getPropertyValue('--dock-lift');
    this.solo.style.setProperty('--dock-x', '0px');
    this.solo.style.setProperty('--dock-lift', '0px');
    this.solo.style.setProperty('--dock-gap', '0px');
    this.solo.style.setProperty('--dock-rise', '0px');

    var pinTop = this.pin.getBoundingClientRect().top;
    var rowBox = row.getBoundingClientRect();
    var rowMid = (rowBox.top + rowBox.bottom) / 2 - pinTop;
    // Both of these are already where they will finish: neither the button
    // nor the copy moves when the gap opens.
    var ctaTop = this.soloCta.getBoundingClientRect().top - pinTop;
    var copyBottom = copy.getBoundingClientRect().bottom - pinTop;

    // So the gap is simply how far the row currently sits below the middle
    // of the space it is meant to sit in the middle of.
    var gap = rowMid - (copyBottom + ctaTop) / 2;
    // NaN-safe, and never let a heading long enough to crowd the row push
    // the row DOWN onto its own button.
    if (!(gap > 0)) gap = 0;

    this.solo.style.setProperty('--dock-gap', gap.toFixed(1) + 'px');
    this.solo.style.setProperty('--dock-rise', (gap / 2).toFixed(1) + 'px');

    if (keepX) this.solo.style.setProperty('--dock-x', keepX);
    if (keepY) this.solo.style.setProperty('--dock-lift', keepY);
  };

  /*
   * Centre the copy and the bag as ONE group, in every phase.
   *
   * The two used to be positioned by unrelated rules: the copy sat on the
   * page grid (which stops growing at 90rem) while the bag was pushed out by
   * a fixed vw offset (which never stops). Past the grid width they simply
   * walked away from each other — 17px between them at 1024, 314px at 1968 —
   * and the pair drifted off-centre as a whole. Measuring both and placing
   * the group makes the gap a constant and keeps the composition centred at
   * any width.
   */
  ScrollStory.prototype.groupFrames = function () {
    if (!this.bag) return;
    var vw = window.innerWidth;
    var bagBox = this.bag.offsetWidth; // untransformed; the scale is per phase
    if (!bagBox) return;

    for (var i = 0; i < this.copyLayers.length; i++) {
      var copy = this.copyLayers[i];
      var f = this.frames[i];
      if (!copy || !f) continue;

      // Centre-aligned phases are already a single stacked composition.
      if (copy.classList.contains('sstory__copy--center')) {
        copy.style.removeProperty('--copy-edge');
        continue;
      }

      var right = copy.classList.contains('sstory__copy--right');
      // Clear first so the computed edge is the CSS gutter rather than
      // whatever the last resize left behind — that gutter is the floor.
      copy.style.removeProperty('--copy-edge');
      var gutter = parseFloat(getComputedStyle(copy)[right ? 'right' : 'left']) || 0;
      var copyW = copy.offsetWidth;
      var bagW = bagBox * f.s;
      if (!copyW) continue;

      var gap = Math.min(Math.max(vw * 0.05, 40), 120);
      // Where the pair will not fit, the gap gives way before the gutter does.
      var room = vw - gutter * 2;
      if (copyW + bagW + gap > room) gap = Math.max(24, room - copyW - bagW);

      var groupW = copyW + gap + bagW;
      var left = Math.max(gutter, (vw - groupW) / 2);
      var bagCentre = left + (right ? bagW / 2 : copyW + gap + bagW / 2);

      copy.style.setProperty('--copy-edge', (right ? vw - (left + groupW) : left).toFixed(1) + 'px');
      f.x = ((bagCentre - vw / 2) / vw) * 100;
    }
  };

  /*
   * Where the heap sits relative to the dock row: how far it drops to reach
   * the bag's feet, and how far right of the bag's centre it gathers.
   *
   * Measured, not constants: the dock row is positioned by CSS (a percentage
   * on tall viewports, bottom-anchored on short ones) while the bag's size is
   * capped in rem, so neither gap is a fixed fraction of anything. A vh
   * constant put the heap 23px off the bottom of a 900x600 screen and left it
   * floating 250px clear of the bag at 2560x1440.
   *
   * Everything is taken relative to the pin, so it holds wherever the page
   * happens to be scrolled when this runs.
   */
  ScrollStory.prototype.measurePile = function () {
    this.pileShift = 0;
    this.pileNudge = 0;
    if (!this.finale || !this.solo || !this.pin || !this.bag) return;
    var row = this.solo.querySelector('.sstory__solo-cubes');
    var frame = this.frames[Math.max(this.n - 2, 0)];
    if (!row || !frame) return;

    // Neutralise the offsets this function is measuring for.
    var keepX = this.solo.style.getPropertyValue('--dock-x');
    var keepY = this.solo.style.getPropertyValue('--dock-lift');
    this.solo.style.setProperty('--dock-x', '0px');
    this.solo.style.setProperty('--dock-lift', '0px');

    var pinBox = this.pin.getBoundingClientRect();
    var rowBox = row.getBoundingClientRect();
    var pinH = pinBox.height;
    var vw = window.innerWidth;
    var rowMid = (rowBox.top + rowBox.bottom) / 2 - pinBox.top;
    // The bag's untransformed centre is the middle of the pin (its CSS
    // margin-top cancels exactly half its height); the phase adds a vh
    // nudge and a scale about that same centre.
    var bagW = this.bag.offsetWidth * frame.s;
    var bagBase = pinH / 2 + (frame.y / 100) * pinH + (this.bag.offsetHeight * frame.s) / 2;

    /*
     * The heap's real extent, cube by cube: the flex row's own box is no
     * guide, because the spots pull the cubes together horizontally, stagger
     * them vertically, and the rotations throw all four back out again. Using
     * the row's height here under-measured the heap by a third and let it
     * settle on top of the shop chip.
     */
    var reachR = -Infinity, reachL = Infinity;
    var reachB = -Infinity, reachT = Infinity;
    for (var i = 0; i < this.dockSize; i++) {
      var cube = this.pngCubes[i];
      var spot = PILE_SPOTS[i % PILE_SPOTS.length];
      var w = cube.offsetWidth, h = cube.offsetHeight;
      var rad = Math.abs(spot.r) * Math.PI / 180;
      var cos = Math.cos(rad), sin = Math.sin(rad);
      var ps = spot.ps || PILE_SCALE;
      var halfW = (w * cos + h * sin) / 2 * ps;
      var halfH = (w * sin + h * cos) / 2 * ps;
      var atX = cube.offsetLeft - row.offsetLeft + w / 2 - row.offsetWidth / 2 + (spot.x / 100) * vw;
      var atY = (spot.y / 100) * pinH;
      reachR = Math.max(reachR, atX + halfW);
      reachL = Math.min(reachL, atX - halfW);
      reachB = Math.max(reachB, atY + halfH);
      reachT = Math.min(reachT, atY - halfH);
    }
    var pileH = reachB - reachT;
    var pileMid = (reachB + reachT) / 2;

    var target = bagBase - pileH * PILE_STRADDLE;
    // Never let the heap hang off the bottom of the stage.
    var maxBottom = pinH - 8;

    // Gather to the bag's right rather than dead centre on it, scaled off the
    // bag so it holds at any width, then kept inside both edges.
    var bagCentre = vw / 2 + (frame.x / 100) * vw;
    var gutter = Math.max(vw * 0.035, 16);
    this.pileNudge = Math.min(bagW * PILE_RIGHT, vw - gutter - bagCentre - reachR);
    var minNudge = gutter - bagCentre - reachL; // never off the other edge
    if (this.pileNudge < minNudge) this.pileNudge = minNudge;

    /*
     * The persistent shop chip lives in the same bottom-right corner the heap
     * banks into, and it is on screen throughout this phase. Rather than shove
     * the heap back off the bag's right side to dodge it, sit the heap a
     * little higher up the bag — that keeps the composition and still never
     * buries a floating CTA under ice.
     */
    if (this.chip) {
      var chipBox = this.chip.getBoundingClientRect();
      var pileR = bagCentre + this.pileNudge + reachR;
      var pileL = bagCentre + this.pileNudge + reachL;
      if (chipBox.width && pileR > chipBox.left - 12 && pileL < chipBox.right + 12) {
        maxBottom = Math.min(maxBottom, chipBox.top - pinBox.top - 10);
      }
    }

    // Both limits act on the heap's real bottom edge, not on its centre.
    var shift = target - pileMid - rowMid;
    var maxShift = maxBottom - reachB - rowMid;
    this.pileShift = Math.max(Math.min(shift, maxShift), 0);

    if (keepX) this.solo.style.setProperty('--dock-x', keepX);
    if (keepY) this.solo.style.setProperty('--dock-lift', keepY);
  };

  ScrollStory.prototype.requestUpdate = function () {
    if (!this.running || this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(this.tick);
  };

  ScrollStory.prototype.update = function () {
    this.rafPending = false;
    if (this.mode !== 'desktop' || !this.track) return;

    var rect = this.track.getBoundingClientRect();
    var total = rect.height - window.innerHeight;
    if (total <= 0) return;

    var progress = clamp01(-rect.top / total);
    var n = this.n;
    var last = n - 1;
    var praw = progress * (n - 1);
    var seg = Math.min(Math.floor(praw), n - 2);
    var u = praw - seg;

    // Dwell: each phase holds its composition, then transitions. Trimmed from
    // 0.26 to 0.2 so the settled states still read but dead zones shrink.
    var hold = 0.2;
    var t = smoothstep(u < hold ? 0 : u > 1 - hold ? 1 : (u - hold) / (1 - 2 * hold));
    var p = seg + t;
    var seam = Math.sin(clamp01(u) * Math.PI);

    // ---- bag: with a colour finale it hands the frame to the cubes ----
    // The bag OWNS every phase up to and including freeze/use/repeat — it is
    // still the hero of that rest view — and only bows out across the
    // transition into the dock phase, drifting up and shrinking as it fades.
    // The cubes are already drifting in from the edges by then, so the two
    // overlap and the handover reads as one move. Floors keep a 2-phase
    // section sane.
    var eS = Math.max(last - 1.0, 0.4);
    var eE = Math.max(last - 0.45, 0.8);
    var exit = this.finale ? smoothstep(clamp01((p - eS) / (eE - eS))) : 0;
    var a = this.frames[seg], b = this.frames[seg + 1] || a;
    var x = a.x + (b.x - a.x) * t;
    var y = a.y + (b.y - a.y) * t;
    var r = a.r + (b.r - a.r) * t;
    var s = a.s + (b.s - a.s) * t;
    this.bag.style.opacity = (1 - exit).toFixed(3);
    this.bag.style.visibility = exit > 0.98 ? 'hidden' : 'visible';
    if (exit <= 0.98) {
      this.bag.style.transform =
        'translate3d(' + x.toFixed(2) + 'vw, ' + (y - exit * 14).toFixed(2) + 'vh, 0) rotate(' +
        r.toFixed(2) + 'deg) scale(' + (s * (1 - seam * 0.045) * (1 - exit * 0.2)).toFixed(3) + ')';
    }

    // ---- background (table lookup, no colour maths per frame) ----
    if (this.bgFill && this.colorTable && this.bgOn) {
      var ci = Math.min(seg, this.colorTable.length - 1);
      this.bgFill.style.backgroundColor = this.colorTable[ci][Math.round(clamp01(t) * LUT_STEPS)];
    }

    // ---- copy: outgoing clears before incoming arrives ----
    for (var i = 0; i < this.copyLayers.length; i++) {
      // Dead band either side of the seam guarantees the two never share the
      // screen at readable opacity.
      var vis = 1 - smoothstep(clamp01((Math.abs(p - i) - 0.1) / 0.34));
      var layer = this.copyLayers[i];
      layer.style.opacity = vis;
      layer.style.setProperty('--copy-shift', ((p - i) * 22).toFixed(1) + 'px');
      layer.style.visibility = vis < 0.04 ? 'hidden' : 'visible';
    }

    // ---- counterpoint images ----
    for (var e = 0; e < this.elementLayers.length; e++) {
      var el = this.elementLayers[e];
      var ev = 1 - smoothstep(clamp01((Math.abs(p - Number(el.dataset.elementLayer)) - 0.1) / 0.34));
      el.style.opacity = ev;
      el.style.visibility = ev < 0.04 ? 'hidden' : 'visible';
    }

    // ---- cubes: opacity + translate only (never filters) ----
    if (this.cubesBg) {
      var cv = 1 - (1 - this.cubesAfter) * smoothstep(clamp01((p - 0.25) / 0.75));
      this.cubesBg.style.opacity = cv * this.baseCubeOpacity;
      this.cubesBg.style.visibility = cv <= 0.01 ? 'hidden' : 'visible';
      if (cv > 0.01) {
        for (var c = 0; c < this.heroCubes.length; c++) {
          var cube = this.heroCubes[c];
          var depth = Number(cube.dataset.depth) || 1;
          cube.style.translate = '0 ' + ((progress - 0.5) * this.cubesTravel * depth).toFixed(2) + 'vh';
        }
      }
    }

    // ---- colour finale: a pile the bag slides into, that drops to the dock --
    if (this.finale) {
      var minEnt = 1;
      /*
       * Two stages, both scheduled as spans off the phase count so this
       * scales to any story length (the floors keep a 2-phase section sane).
       *
       *   gather  the cubes drop in one by one and build a heap, finishing
       *           just before the freeze/use/repeat rest — so the bag, which
       *           travels to that side across the same stretch, slides
       *           straight into a pile that is already waiting for it.
       *   drop    the heap breaks and falls DOWN into the dock row on the
       *           colour phase, where the same four cubes go clear and start
       *           cycling. It runs after the bag has begun bowing out, so the
       *           cubes are handed the frame rather than fighting for it.
       *
       * The heap is carried horizontally to the bag by --dock-x, read from
       * the bag's own keyframe for that phase, so it lands on the bag at any
       * viewport width instead of at a guessed offset.
       */
      // Opens only once the previous phase's copy has faded: the heap builds
      // on the side the bag is heading for, which is the side that phase put
      // its copy on, so starting any earlier drops ice onto live type.
      var gStart = Math.max(last - 1.55, 0.25);
      var gEnd = Math.max(last - 1.1, gStart + 0.3);
      var sStart = Math.max(last - 0.7, gEnd + 0.15);
      var sEnd = Math.max(last - 0.25, sStart + 0.3);
      var pileFrame = this.frames[Math.max(last - 1, 0)];
      var pileX = pileFrame ? pileFrame.x : 0;
      var lastQ = Math.max(this.dockSize - 1, 1);
      var anyGather = 0;
      for (var q = 0; q < this.pngCubes.length; q++) {
        var spot = PILE_SPOTS[q % PILE_SPOTS.length];
        var stagger = clamp01(q / lastQ);
        // Staggered both ways, so the heap builds cube by cube and breaks
        // up the same way rather than moving as one block.
        var gth = smoothstep(clamp01(
          (p - (gStart + (gEnd - gStart) * 0.45 * stagger)) / ((gEnd - gStart) * 0.55)));
        var ent = smoothstep(clamp01(
          (p - (sStart + (sEnd - sStart) * 0.3 * stagger)) / ((sEnd - sStart) * 0.7)));
        this.cubeEnts[q] = ent;
        if (ent < minEnt) minEnt = ent;
        if (gth > anyGather) anyGather = gth;
        // A sine arc bows the fall so it reads as tumbling out of the heap
        // rather than sliding along a straight line.
        var arc = Math.sin(ent * Math.PI);
        var pileS = spot.ps || PILE_SCALE;
        var cube = this.pngCubes[q];
        cube.style.opacity = gth.toFixed(3);
        cube.style.transform =
          'translate3d(' + (spot.x * (1 - ent)).toFixed(2) + 'vw, ' +
          (spot.y * (1 - ent) - (1 - gth) * PILE_DROP + arc * 5).toFixed(2) + 'vh, 0) rotate(' +
          (spot.r * (1 - ent) - arc * 10 + spot.rest * ent).toFixed(1) + 'deg) scale(' +
          (pileS + (1 - pileS) * ent).toFixed(3) + ')';
      }

      this.solo.style.opacity = anyGather > 0 ? 1 : 0;
      this.solo.style.visibility = anyGather > 0 ? 'visible' : 'hidden';
      // The heap rides with the bag and sits above the dock row; as the cubes
      // drop, the group slides back to centre and down into place.
      var held = 1 - smoothstep(clamp01((p - sStart) / Math.max(sEnd - sStart, 0.12)));
      this.solo.style.setProperty('--dock-x',
        'calc(' + (pileX * held).toFixed(2) + 'vw + ' + (this.pileNudge * held).toFixed(1) + 'px)');
      this.solo.style.setProperty('--dock-lift', (this.pileShift * held).toFixed(1) + 'px');

      /*
       * The morph. Ordered before setFlip for the same reason the old drain
       * was: landing straight on the dock (reload, End key, editor reload)
       * has to produce coloured cubes rather than the markup default.
       */
      if (!this.flipPainted) {
        for (var fc = 0; fc < this.dockSize; fc++) {
          // Quantised so this writes a few dozen times per flight rather
          // than once per cube per frame; at rest it never writes at all.
          var qe = Math.round((this.cubeEnts[fc] || 0) * 24) / 24;
          if (this.landKeys[fc] !== qe) this.paintLanding(fc, qe);
        }
      }

      // The cycle starts only once every cube has docked, and never while
      // the section is parked off screen. The first tick waits a full
      // FLIP_MS, which is what makes the landing readable.
      this.setFlip(this.running && minEnt >= 1 && p > last - 0.2);

      // Dock CTA joins the tab order only while it is actually visible.
      var ctaOn = p > last - 0.12;
      if (ctaOn !== this.ctaShown) {
        this.ctaShown = ctaOn;
        if (this.soloCta) {
          this.soloCta.style.opacity = ctaOn ? 1 : 0;
          this.soloCta.style.visibility = ctaOn ? 'visible' : 'hidden';
          this.soloCta.setAttribute('tabindex', ctaOn ? '0' : '-1');
          this.soloCta.setAttribute('aria-hidden', ctaOn ? 'false' : 'true');
        }
      }
    }

    // ---- shop chip: only once the hero CTA is gone, so it never duplicates it
    if (this.chip) {
      var chipOn = p > 0.55;
      if (chipOn !== this.chipShown) {
        this.chipShown = chipOn;
        this.chip.classList.toggle('is-shown', chipOn);
        this.chip.setAttribute('tabindex', chipOn ? '0' : '-1');
        this.chip.setAttribute('aria-hidden', chipOn ? 'false' : 'true');
      }
    }
  };

  // ------------------------------------------------------- colour cycle ----
  // Timed, not scroll-driven: once the cubes dock, colour keeps moving even
  // with the page at rest. Interval-gated by setFlip so it never runs while
  // the dock is off screen, the tab is hidden, or the cubes are in flight.
  ScrollStory.prototype.setFlip = function (on) {
    if (on && !this.flipTimer && this.skinCount > 1 && this.pngCubes.length) {
      var self = this;
      this.flipTimer = setInterval(function () { self.applyFlip(); }, FLIP_MS);
    } else if (!on && this.flipTimer) {
      clearInterval(this.flipTimer);
      this.flipTimer = null;
      this.flipPainted = false;
      // resetConveyor puts every cube back to blue, which is both the flight
      // colour and the resting colour now, so scrolling back out needs
      // nothing further.
      this.resetConveyor();
    }
  };

  /*
   * Put the row back the way it shipped: original DOM order, no offsets, the
   * first four showing and the fifth parked off the right at zero.
   *
   * The order matters beyond tidiness. PILE_SPOTS is indexed positionally and
   * `measurePile` measures the first four slots, so a row left in conveyor
   * order would fly the heap in from the wrong places on the way back down.
   */
  ScrollStory.prototype.resetConveyor = function () {
    this.slideOpen = false;
    this.dealLanding();
    for (var i = 0; i < this.cubeSlots.length; i++) {
      var slot = this.cubeSlots[i];
      slot.classList.remove('is-sliding');
      slot.style.removeProperty('--slide');
      slot.style.removeProperty('--drop');
      slot.style.opacity = i < this.dockSize ? '1' : '0';
      if (this.cubeRow) this.cubeRow.appendChild(slot); // re-append == reorder
      var skins = this.cubeSkins[i] || [];
      for (var j = 0; j < skins.length; j++) skins[j].style.opacity = '0';
      // Blue is the base: it is what the cube is in flight, what it lands
      // as, and what it returns to if the cycle is wound back.
      if (this.cubeFlights[i]) this.cubeFlights[i].style.opacity = '1';
    }
  };

  /*
   * Deal each docking cube the colour it will morph into on the way in, and
   * point the conveyor at a colour none of them got.
   *
   * Distinct, not merely random: four independent draws from eight colours
   * repeat about as often as not, and two identical cubes in a row of four
   * reads as a bug rather than a shuffle. A partial Fisher-Yates over the
   * indices gives distinctness for free.
   *
   * Re-dealt on every reset, so scrolling back out and in again brings a
   * different four rather than the same set every time.
   */
  ScrollStory.prototype.dealLanding = function () {
    var n = this.skinCount;
    this.landKeys = [];
    this.landIdx = [];
    if (!n) { this.flipIdx = 0; return; }

    var bag = [];
    for (var i = 0; i < n; i++) bag.push(i);
    for (var k = 0; k < bag.length - 1; k++) {
      var r = k + Math.floor(Math.random() * (bag.length - k));
      var t = bag[k]; bag[k] = bag[r]; bag[r] = t;
    }
    this.landIdx = bag.slice(0, Math.min(this.dockSize, n));
    // The conveyor picks up on a colour that is not already on the row, so
    // its first turn actually changes something.
    this.flipIdx = this.landIdx.length < n ? bag[this.landIdx.length] : 0;
  };

  /*
   * Blue -> its dealt colour, in step with the cube's own arrival.
   *
   * Blue holds at full until the colour is past halfway rather than the two
   * crossing at half alpha each: the cutouts are separate photographs whose
   * outlines do not agree, and overlapping them this way keeps the cube
   * solid through the swap instead of letting it thin out. It happens while
   * the cube is still flying, scaling and rotating into its slot, which is
   * what makes it read as the ice taking colour rather than as a dissolve.
   */
  ScrollStory.prototype.paintLanding = function (i, qe) {
    this.landKeys[i] = qe;
    var skins = this.cubeSkins[i] || [];
    var idx = this.landIdx[i];
    if (idx == null || !skins[idx]) return;
    skins[idx].style.opacity = qe;
    if (this.cubeFlights[i]) {
      this.cubeFlights[i].style.opacity =
        qe < 0.55 ? 1 : (1 - (qe - 0.55) / 0.45).toFixed(3);
    }
  };

  // Show exactly one cutout on a cube: `idx` picks a cycle colour, or -1
  // hands the cube back to the blue it flew in as.
  ScrollStory.prototype.setCubeSkin = function (slot, idx) {
    var at = this.cubeSlots.indexOf(slot);
    if (at < 0) return;
    var skins = this.cubeSkins[at] || [];
    for (var j = 0; j < skins.length; j++) {
      skins[j].style.opacity = j === idx ? '1' : '0';
    }
    // The colour is opaque and covers it, but leaving blue on underneath
    // would put a second silhouette behind it — the rim problem, back again.
    if (this.cubeFlights[at]) this.cubeFlights[at].style.opacity = idx >= 0 ? '0' : '1';
  };

  /*
   * Land the move that is already on screen, instantly and invisibly.
   *
   * After a slide every slot sits one place to the left of where the flex row
   * would put it, so moving the leftmost to the end and clearing every offset
   * in the same breath is an IDENTITY: each remaining slot's natural position
   * is exactly where the transform had it. The only thing that actually moves
   * is the spent slot, from off the left to off the right, at opacity 0.
   */
  ScrollStory.prototype.commitSlide = function () {
    if (!this.slideOpen || !this.cubeRow) return;
    this.slideOpen = false;
    var slots = [].slice.call(this.cubeRow.children);
    for (var i = 0; i < slots.length; i++) {
      slots[i].classList.remove('is-sliding'); // kills the transition first,
      slots[i].style.removeProperty('--slide'); // so these two do not animate
      slots[i].style.removeProperty('--drop');
    }
    slots[0].style.opacity = '0';
    this.cubeRow.appendChild(slots[0]);
  };

  /*
   * One turn of the conveyor: a new colour drops in on the right, everything
   * scoots one place left, and the cube on the far left slides out and fades
   * as it goes. Push, pop.
   *
   * Nothing cross-fades. That was the point of rebuilding it this way — the
   * eight cutouts are eight separate photographs whose outlines disagree, so
   * dissolving one into another made the shape fight itself. A cube here only
   * ever changes colour off-stage, and the only thing that moves on screen is
   * the cube, whole.
   */
  ScrollStory.prototype.applyFlip = function () {
    if (!this.cubeRow) return;
    // Land the previous turn before starting the next. Doing it here rather
    // than on a timer of its own means the whole cycle runs off ONE interval,
    // and a move can never be committed while it is still animating: the
    // slide is 0.7s and the interval is FLIP_MS.
    this.commitSlide();
    // First commit hands the row from the clear drain over to the cycle.
    this.flipPainted = true;

    var slots = [].slice.call(this.cubeRow.children);
    if (slots.length < 2) return;
    var onDeck = slots[slots.length - 1];
    var leaving = slots[0];

    // Measured, not derived from the CSS: --cube-w is a min() of a rem and a
    // vw, so the only honest source for one slot's pitch is the gap between
    // two of them as laid out right now.
    var step = slots[1].offsetLeft - slots[0].offsetLeft;
    if (!step) return;

    // Load the incoming cube while it is invisible and lift it clear, then
    // let the reflow make that the state the transition starts FROM.
    this.setCubeSkin(onDeck, this.flipIdx % Math.max(this.skinCount, 1));
    onDeck.classList.remove('is-sliding');
    onDeck.style.setProperty('--drop', (-step * 0.42).toFixed(1) + 'px');
    onDeck.style.opacity = '0';
    void onDeck.offsetWidth;

    for (var i = 0; i < slots.length; i++) {
      slots[i].classList.add('is-sliding');
      slots[i].style.setProperty('--slide', (-step).toFixed(1) + 'px');
    }
    onDeck.style.setProperty('--drop', '0px');
    onDeck.style.opacity = '1';
    // The one leaving goes further than the rest so it reads as being pushed
    // off the end rather than merely stopping a slot early.
    leaving.style.setProperty('--slide', (-step * 1.45).toFixed(1) + 'px');
    leaving.style.opacity = '0';

    this.slideOpen = true;
    this.flipIdx = (this.flipIdx + 1) % Math.max(this.skinCount, 1);
  };

  // -------------------------------------------------------------- mobile ----
  // No pinning, no scrubbing: the stacked sections simply ease in once.
  ScrollStory.prototype.mountMobile = function () {
    if (!this.staticBands.length || !('IntersectionObserver' in window)) return;
    var self = this;
    this.mobileIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        self.mobileIO.unobserve(entry.target); // one-shot
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    this.staticBands.forEach(function (band) {
      band.classList.add('is-anim');
      self.mobileIO.observe(band);
    });
  };

  // ------------------------------------------------------------- teardown ---
  ScrollStory.prototype.teardownMode = function () {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    clearTimeout(this.resizeTimer);
    if (this.io) { this.io.disconnect(); this.io = null; }
    if (this.mobileIO) { this.mobileIO.disconnect(); this.mobileIO = null; }
    this.staticBands.forEach(function (band) { band.classList.remove('is-anim', 'is-in'); });
    // Leaving motion mode hands the copy back to the page grid.
    this.copyLayers.forEach(function (c) { c.style.removeProperty('--copy-edge'); });
    this.setFlip(false);
    this.flipIdx = 0;
    this.flipPainted = false;
    // Measured placement belongs to desktop motion mode; hand the dock back
    // to the stylesheet's own fallback on the way out.
    if (this.solo) {
      this.solo.style.removeProperty('--dock-gap');
      this.solo.style.removeProperty('--dock-rise');
    }
    this.running = false;
    this.rafPending = false;
    this.chipShown = undefined;
    this.ctaShown = undefined;
    this.root.classList.remove('sstory--animating');
  };

  ScrollStory.prototype.destroy = function () {
    this.teardownMode();
    this.removeMQListener(this.motionMQ, this.onModeChange);
    this.removeMQListener(this.reducedMQ, this.onModeChange);
    this.mode = null;
  };

  // ----------------------------------------------------------------- boot ---
  function init(scope) {
    (scope || document).querySelectorAll('[data-sstory]').forEach(function (root) {
      if (root.__sstory) return;
      root.__sstory = new ScrollStory(root);
      instances.push(root.__sstory);
    });
  }

  function destroyIn(scope) {
    instances = instances.filter(function (inst) {
      if (scope && !scope.contains(inst.root)) return true;
      inst.destroy();
      inst.root.__sstory = null;
      return false;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Theme editor: rebuild cleanly instead of stacking a second controller.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroyIn(e.target); });
})();
